// Claims stay inside .seo/truth.md: the figure prices are the resin-prototype
// and production terms confirmed 2026-09-29 (also on /custom-vinyl-figures-
// prototyping/), the trophy row reads ./custom-trophy pricing, and likenesses,
// larger sizes and extra detail stay "quoted". Pets confirmed by the owner
// 2026-10-31 ("we do a lot of pets"). Bobbleheads have their own demand-test
// page, quoted per project (owner policy 2026-11-01); no celebrity promise
// until the studio answers .seo/needs-you.md NY-19. Buyer
// questions come from mascot and likeness inquiries (Aug 2025–Oct 2026) and the
// 2026-10-29 AI-visibility prompts #36–#38.
const { pricing: trophyPricing } = require('./custom-trophy')

const figurePricing = {
  prototype: 450,
  prototypeHeight: '6-inch',
  prototypeColors: 3,
  prototypeWeeks: '3–4 weeks',
  revisions: '1–2',
  resinRun: '1 to 100',
  resinMinimumBudget: 450,
  vinylMinimum: 300,
  vinylMold: '1,500',
}

const customFigurines = {
  path: '/services/custom-figurines/',
  reviewedDate: '2026-10-31',
  reviewedDateLabel: 'October 31, 2026',
  title: 'Custom Figurines from Photos: People & Pets | PrintAWorld',
  description: `Custom figurines of people, pets and mascots, sculpted from your photos and hand-painted in New York. Simple ${figurePricing.prototypeHeight} figures about $${figurePricing.prototype}.`,
  answer: `PrintAWorld sculpts custom figurines in New York from photos of a person or a pet, a company mascot, a logo or a sketch. We model the figure in 3D, send it to you to approve, then print it in resin and paint it by hand. A simple ${figurePricing.prototypeHeight} figure is about $${figurePricing.prototype} including the 3D model and paint; a realistic likeness, a bigger size or extra detail is quoted. Order one, a resin run of ${figurePricing.resinRun}, or ${figurePricing.vinylMinimum}+ in molded vinyl, shipped anywhere in the U.S.`,
}

const subjects = [
  {
    title: 'Your company mascot',
    body: 'A desk figure for the team, a client gift, a trade-show centerpiece. Only have the flat logo or one front view? Send it; we design the sides and back and show you the 3D model before printing.',
  },
  {
    title: 'A real person',
    body: 'A founder, a retiring colleague, a creator, yourself. We sculpt from photos. A stylized figure with simple shapes costs less than a realistic portrait, and we quote both. Want the head to bobble?',
    link: ['Custom bobbleheads', '/services/custom-figurines/bobbleheads/'],
  },
  {
    title: 'Your pet',
    body: 'Dogs, cats and other pets, sculpted from your photos: a desk-sized portrait, a gift, or a keepsake of a pet you’ve lost. Fur, markings and breed details are what we quote on.',
    link: ['Pet figurines', '/services/custom-figurines/pets/'],
  },
  {
    title: 'A brand character',
    body: 'The character from your packaging, app or ads, made into an object people can hold. Useful for pitches and launches, and as the sample before a larger run.',
  },
  {
    title: 'The mascot as an award',
    body: 'Put the figure on a base with an engraved plate and it becomes the company trophy. Awards follow our trophy pricing.',
    link: ['Custom trophies', '/services/custom-trophy/'],
  },
  {
    title: 'A creator’s merch sample',
    body: 'One painted figure to show your audience or a manufacturer before you commit to production. Resin for small runs, molded vinyl from 300 pieces.',
  },
  {
    title: 'An original character',
    body: 'Designer toys and art figures in limited editions, from your sketch or AI concept.',
    link: [
      'Custom vinyl and resin figures',
      '/custom-vinyl-figures-prototyping/',
    ],
  },
]

const photoTips = [
  'A close, front-facing photo of the face in even light. A face that is small in a group or action shot doesn’t carry enough detail to sculpt from.',
  'A side view of the head, and the back if hair or a hat matters.',
  'A full-length photo of the outfit, shoes and any logo the figure should wear.',
  'The pose you want, even if it’s from a different photo or a stock image.',
  'For a pet: the face from the front at eye level, a side view, and the full body standing, so we can see markings and proportions.',
  'For a mascot: every view you have, plus the brand colors.',
]

const routes = [
  {
    name: 'Simple figure',
    price: `About $${figurePricing.prototype}`,
    detail: `One ${figurePricing.prototypeHeight} resin figure in a fixed pose, up to ${figurePricing.prototypeColors} solid paint colors, 3D model and ${figurePricing.revisions} revisions included. Typically ${figurePricing.prototypeWeeks}.`,
  },
  {
    name: 'Likeness or detailed figure',
    price: 'Quoted after review',
    detail:
      'Realistic faces, more colors, accessories and sizes above 6 inches take more sculpting and painting. Send photos and the size for a price.',
  },
  {
    name: 'Small resin run',
    price: `${figurePricing.resinRun} pieces`,
    detail: `Priced per piece for the quantity. Resin projects start at a $${figurePricing.resinMinimumBudget} total budget.`,
  },
  {
    name: 'Molded vinyl',
    price: `From ${figurePricing.vinylMinimum} pieces`,
    detail: `A mold for each character, typically about $${figurePricing.vinylMold}, plus production.`,
  },
  {
    name: 'Mascot award on a base',
    price: `From $${trophyPricing.from} each`,
    detail: `Trophy pricing, plus a one-time $${trophyPricing.modelingFee} 3D modeling fee for most designs.`,
  },
]

const faqs = [
  {
    question: 'How much does a custom figurine cost?',
    answer: `A simple ${figurePricing.prototypeHeight} resin figurine is about $${figurePricing.prototype}, including the 3D model, printing, painting in up to ${figurePricing.prototypeColors} solid colors and ${figurePricing.revisions} design revisions. A realistic likeness of a person, a larger figure, extra colors or accessories need more sculpting and painting, so we quote them after seeing your photos. Mascot awards on a base start at $${trophyPricing.from} each plus a one-time $${trophyPricing.modelingFee} modeling fee.`,
  },
  {
    question: 'Can you make a figurine from a photo?',
    answer:
      'Yes. Send photos of the person or a picture of the character. We model the figure in 3D from your photos and send you the model to approve before anything is printed. Clear, close photos of the face from the front and side make the closest likeness.',
  },
  {
    question: 'Can you make a figurine of my pet?',
    answer:
      'Yes, we make a lot of pet figurines. Send clear photos of your pet’s face from the front and side and one of the whole body, plus the size you want. We model it in 3D and send it to you to approve before printing and hand-painting. Fur texture, markings and pose decide the price, so pet figures are quoted from your photos.',
  },
  {
    question: 'Can you turn our company mascot into a figure?',
    answer:
      'Yes. Send the mascot artwork, every view you have, and your brand colors. If you only have a front view, we design the sides and back and you approve the 3D model first. Mascot figures can be desk figures, client gifts, or awards on an engraved base.',
  },
  {
    question: 'Can I order just one figurine?',
    answer: `Yes. One figure is fine, and resin runs go up to 100 pieces. Resin projects start at a $${figurePricing.resinMinimumBudget} total budget. For ${figurePricing.vinylMinimum} or more, molded vinyl costs less per piece but needs a mold for each character, typically about $${figurePricing.vinylMold}.`,
  },
  {
    question: 'How long does a custom figurine take?',
    answer: `A simple figure typically takes ${figurePricing.prototypeWeeks}, including the 3D model and your revisions. Likenesses and larger figures take longer. Tell us the date you need it in hand and we’ll tell you what fits.`,
  },
  {
    question: 'Can you make it from an AI-generated image?',
    answer:
      'Yes. An AI image is a good way to show the style you want, such as a chibi version of yourself or your mascot in a pose. We follow the image closely and tell you what has to change to make it physical. One subject on a plain background, thick shapes and a few solid colors turn out closest to the picture.',
  },
  {
    question: 'Do you make action figures with moving joints?',
    answer:
      'No. Our figures are sculpted in one fixed pose. We don’t make articulated action figures.',
  },
  {
    question:
      'Why does a studio figure cost more than an online photo figurine?',
    answer:
      'Online photo-figurine and bobblehead shops sell figures for under $100, often by fitting a sculpted head to a ready-made body. That works well for a quick gift. A studio figure is modeled from scratch for your person or mascot, approved by you in 3D before printing, and painted by hand, which is what brands, awards and merch samples usually need.',
  },
  {
    question: 'Do you ship?',
    answer:
      'Yes, anywhere in the United States. Pickup from our New York studio is available by appointment.',
  },
]

module.exports = {
  customFigurines,
  figurePricing,
  subjects,
  photoTips,
  routes,
  faqs,
}
